import { CalendarToday, Delete, Edit } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import CommonDialog from '../../../common/CommonDialog';
import { useDeleteDisabledSlot, useGetDisabledSlots } from '../../../services/core/settings/settings.hooks';

const styles = {
    tableContainer: {
        marginTop: '16px',
        boxShadow: 'none',
        border: 'none',
        borderRadius: '8px',
        overflow: 'hidden',
    },
    tableHeader: {
        backgroundColor: '#F5F5F5',
        height: '40px',
        '& .MuiTableCell-root': {
            border: 'none',
            borderBottom: 'none',
            padding: '0 16px',
            fontWeight: 600,
            fontSize: '14px',
            color: 'rgba(0, 0, 0, 0.87)',
            letterSpacing: '0.15px',
            height: '40px',
        },
    },
    tableRow: {
        '&:hover': {
            backgroundColor: '#FAFAFA',
        },
        '& .MuiTableCell-root': {
            border: 'none',
            borderBottom: 'none',
            padding: '0 16px',
            fontSize: '16px',
            fontWeight: 500,
            color: 'rgba(0, 0, 0, 0.87)',
            height: '46px',
        },
    },
    dateCell: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    calendarIcon: {
        color: '#006CDD',
        fontSize: '20px',
    },
    dateText: {
        fontSize: '16px',
        fontWeight: 500,
        color: 'rgba(0, 0, 0, 0.87)',
        letterSpacing: '0.15px',
    },
    disabledSlotsText: {
        fontSize: '16px',
        fontWeight: 500,
        color: 'rgba(0, 0, 0, 0.87)',
        letterSpacing: '0.15px',
        cursor: 'default',
    },
    timeRangeText: {
        fontSize: '16px',
        fontWeight: 500,
        color: 'rgba(0, 0, 0, 0.87)',
        letterSpacing: '0.15px',
    },
    actionCell: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        justifyContent: 'flex-end',
    },
    editIcon: {
        color: '#646466',
        fontSize: '24px',
    },
    deleteIcon: {
        color: '#D32F2F',
        fontSize: '24px',
    },
    iconButton: {
        padding: '4px',
        '&:hover': {
            backgroundColor: 'transparent',
        },
    },
    emptyState: {
        padding: '40px',
        textAlign: 'center',
        color: '#757575',
    },
};

const DisabledSlotsTable = ({ onEdit }) => {
    // Fetch disabled slots from API
    const disabledSlotsQuery = useGetDisabledSlots();
    const deleteDisabledSlotMutation = useDeleteDisabledSlot();
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const formatDate = (dateString) => {
        return dayjs(dateString).format('DD-MMM-YYYY');
    };

    const formatDateTime = (dateTimeString) => {
        if (!dateTimeString) return '';
        return dayjs(dateTimeString).format('hh:mm A');
    };

    const renderDisabledSlots = (slots) => {
        if (!slots || slots.length === 0) return null;

        if (slots.length === 1) {
            // Show time range for single slot
            const slot = slots[0];
            const startTime = formatDateTime(slot.startTime);
            const endTime = formatDateTime(slot.endTime);

            if (!startTime || !endTime) return null;

            return (
                <Typography sx={styles.timeRangeText}>
                    {startTime} - {endTime}
                </Typography>
            );
        }

        // Show count for multiple slots
        return <Typography sx={styles.disabledSlotsText}>{slots.length}</Typography>;
    };

    const handleEdit = (item) => {
        if (onEdit) {
            onEdit(item);
        }
    };

    const handleDelete = (item) => {
        setItemToDelete(item);
        setDeleteModalOpen(true);
    };

    const handleDeleteCancel = () => {
        setDeleteModalOpen(false);
        setItemToDelete(null);
    };

    const handleDeleteConfirm = () => {
        if (itemToDelete?.id) {
            deleteDisabledSlotMutation.mutate(itemToDelete.id, {
                onSuccess: () => {
                    setDeleteModalOpen(false);
                    setItemToDelete(null);
                },
            });
        }
    };

    // Transform API response to match expected format
    const transformApiData = (apiData) => {
        if (!apiData || !Array.isArray(apiData)) return [];

        // API response structure: [{ _id, date, slots: [{ startTime, endTime, _id }], ... }]
        return apiData.map((item) => {
            const date = dayjs(item.date).format('YYYY-MM-DD');
            return {
                id: item._id,
                date: date,
                disabledSlots: item.slots || [],
            };
        });
    };

    const disabledSlots = transformApiData(disabledSlotsQuery.data);
    console.log('🚀 ~ DisabledSlotsTable ~ disabledSlots:', disabledSlots);

    // Render table content based on state
    const renderTableContent = () => {
        if (disabledSlotsQuery.isLoading) {
            return (
                <TableContainer sx={styles.tableContainer}>
                    <Box sx={{ ...styles.emptyState, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <CircularProgress />
                    </Box>
                </TableContainer>
            );
        }

        if (disabledSlotsQuery.isError) {
            return (
                <TableContainer sx={styles.tableContainer}>
                    <Box sx={styles.emptyState}>
                        <Typography>Error loading disabled slots. Please try again.</Typography>
                    </Box>
                </TableContainer>
            );
        }

        if (!disabledSlots || disabledSlots.length === 0) {
            return <></>;
        }

        return (
            <TableContainer sx={styles.tableContainer}>
                <Table
                    sx={{
                        '& .MuiTableCell-root': {
                            border: 'none',
                        },
                    }}
                >
                    <TableHead>
                        <TableRow sx={styles.tableHeader}>
                            <TableCell>Date</TableCell>
                            <TableCell>Disabled Slots</TableCell>
                            <TableCell align="right" sx={{ width: '100px' }}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {disabledSlots.map((item) => (
                            <TableRow key={item.id} sx={styles.tableRow}>
                                <TableCell>
                                    <Box sx={styles.dateCell}>
                                        <CalendarToday sx={styles.calendarIcon} />
                                        <Typography sx={styles.dateText}>{formatDate(item.date)}</Typography>
                                    </Box>
                                </TableCell>
                                <TableCell>{renderDisabledSlots(item.disabledSlots)}</TableCell>
                                <TableCell>
                                    <Box sx={styles.actionCell}>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleEdit(item)}
                                            sx={styles.iconButton}
                                            disabled={deleteDisabledSlotMutation.isPending}
                                        >
                                            <Edit sx={styles.editIcon} />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDelete(item)}
                                            sx={styles.iconButton}
                                            disabled={deleteDisabledSlotMutation.isPending}
                                        >
                                            <Delete sx={styles.deleteIcon} />
                                        </IconButton>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    return (
        <>
            {renderTableContent()}

            {/* Delete Confirmation Dialog */}
            <CommonDialog
                open={deleteModalOpen}
                onClose={handleDeleteCancel}
                onSave={handleDeleteConfirm}
                loader={deleteDisabledSlotMutation.isPending}
                dialogTitle="Delete disabled slot?"
                dialogSubtitle={
                    itemToDelete
                        ? `Are you sure you want to delete ${itemToDelete.disabledSlots?.length || 0} disabled slot${(itemToDelete.disabledSlots?.length || 0) > 1 ? 's' : ''} on ${formatDate(itemToDelete.date)}`
                        : ''
                }
                saveButtonText="DELETE"
                saveButtonType="error"
                cancelButtonText="CANCEL"
            />
        </>
    );
};

export default DisabledSlotsTable;
